/**
 * نمای پرونده — آنچه سیستم از توصیف کاربر فهمیده.
 *
 * نمایش این نما یک الزام شفافیت است: کاربر باید ببیند برداشت سیستم چه بوده،
 * وگرنه نمی‌فهمد چرا برآورد به آن سمت رفته.
 *
 * ردیف‌ها و برچسب‌هایشان از backend می‌آیند (`GET /consultations/{id}/case`)،
 * پس ترتیب و متن فارسی در یک جا می‌ماند.
 */

import { useQuery } from '@tanstack/react-query'
import { FileSearch } from 'lucide-react'

import { Card, DetailRows, LoadingRows, SectionHeader } from '@/components/ui/primitives'
import { fetchCaseRows } from '@/lib/api/endpoints'
import { orDash } from '@/lib/format'
import { queryKeys } from '@/lib/queries'

import type { ClarificationAnswer } from '@/lib/types'

interface CaseViewProps {
  consultationId: string
  rawUserInput: string
  clarification: ClarificationAnswer[]
}

export const CaseView = ({ consultationId, rawUserInput, clarification }: CaseViewProps) => {
  const { data: rows, isPending } = useQuery({
    queryKey: queryKeys.caseRows(consultationId),
    queryFn: () => fetchCaseRows(consultationId),
  })

  return (
    <div className="space-y-4">
      <Card>
        <SectionHeader title="متن اصلی شما" icon={<FileSearch className="size-4" />} />
        <p className="p-4 text-sm leading-relaxed whitespace-pre-wrap text-ink-muted">
          {rawUserInput}
        </p>
      </Card>

      <Card>
        <SectionHeader
          title="پرونده‌ی ساختاریافته"
          caption="آنچه سیستم از توصیف شما فهمیده است."
        />
        {isPending ? (
          <LoadingRows rows={6} />
        ) : (
          <div className="px-4 py-2">
            <DetailRows
              rows={Object.entries(rows ?? {}).map(([label, value]) => [label, orDash(value)])}
            />
          </div>
        )}
      </Card>

      {clarification.length > 0 && (
        <Card>
          <SectionHeader title="پاسخ‌های شفاف‌سازی" />
          <ul className="divide-y divide-line">
            {clarification.map((answer) => (
              <li key={answer.question} className="p-3 text-sm">
                <p className="text-ink">{answer.question}</p>
                <p className="mt-1 text-xs text-ink-muted">
                  {answer.is_unknown ? 'نامعلوم — کاربر اطلاعش را نداشت' : orDash(answer.answer)}
                </p>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  )
}
